import { useState } from "react";
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Tooltip,
  Typography,
  alpha,
  lighten,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { Periodo, StatusMeta } from "../../pages/suporte/types";
import { styless } from "../../pages/suporte/styles";

const FORMATO = "YYYY-MM-DD";

const PRESETS: { label: string; resolver: () => Periodo }[] = [
  {
    label: "Últimos 7 dias",
    resolver: () => ({
      inicio: dayjs().subtract(6, "day").format(FORMATO),
      fim: dayjs().format(FORMATO),
    }),
  },
  {
    label: "Últimos 30 dias",
    resolver: () => ({
      inicio: dayjs().subtract(29, "day").format(FORMATO),
      fim: dayjs().format(FORMATO),
    }),
  },
  {
    label: "Este mês",
    resolver: () => ({
      inicio: dayjs().startOf("month").format(FORMATO),
      fim: dayjs().endOf("month").format(FORMATO),
    }),
  },
];

interface FiltersBarProps {
  busca: string;
  onBusca: (valor: string) => void;
  status: StatusMeta[];
  onStatus: (valor: StatusMeta[]) => void;
  opcoesStatus: StatusMeta[];
  mostrarStatus: boolean;
  periodo: Periodo;
  onPeriodo: (valor: Periodo) => void;
  ativos: number;
  onLimpar: () => void;
}

export default function FiltersBar({
  busca,
  onBusca,
  status,
  onStatus,
  opcoesStatus,
  mostrarStatus,
  periodo,
  onPeriodo,
  ativos,
  onLimpar,
}: FiltersBarProps) {
  const theme = useTheme();
  const styles = styless(theme);
  const dark = theme.palette.mode === "dark";
  const [ancora, setAncora] = useState<HTMLElement | null>(null);

  const periodoAtivo = !!(periodo.inicio || periodo.fim);
  const rotuloPeriodo = periodoAtivo
    ? `${periodo.inicio ? dayjs(periodo.inicio).format("DD/MM/YY") : "…"} – ${
        periodo.fim ? dayjs(periodo.fim).format("DD/MM/YY") : "…"
      }`
    : "Período";

  return (
    <>
      <TextField
        value={busca}
        onChange={(e) => onBusca(e.target.value)}
        placeholder="Buscar por código ou título"
        autoComplete="off"
        sx={styles.busca}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: busca ? (
            <InputAdornment position="end">
              <ClearIcon
                fontSize="small"
                sx={{ cursor: "pointer" }}
                onClick={() => onBusca("")}
              />
            </InputAdornment>
          ) : null,
        }}
      />

      {mostrarStatus && (
        <Autocomplete
          multiple
          size="small"
          options={opcoesStatus}
          value={status}
          onChange={(_e, valor) => onStatus(valor)}
          getOptionLabel={(opcao) => opcao.label}
          isOptionEqualToValue={(opcao, valor) => opcao.key === valor.key}
          limitTags={1}
          disableCloseOnSelect
          sx={styles.filtroStatus}
          renderOption={(props, opcao) => (
            <Box component="li" {...props} sx={{ gap: 1 }}>
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.chip[opcao.chipKey],
                }}
              />
              {opcao.label}
            </Box>
          )}
          renderTags={(valor, getTagProps) =>
            valor.map((opcao, indice) => {
              const { key, ...tagProps } = getTagProps({ index: indice });
              const cor = theme.palette.chip[opcao.chipKey];
              return (
                <Chip
                  key={key}
                  size="small"
                  label={opcao.label}
                  {...tagProps}
                  sx={{
                    height: 22,
                    fontWeight: 600,
                    backgroundColor: alpha(cor, dark ? 0.32 : 0.16),
                    color: dark ? lighten(cor, 0.4) : cor,
                  }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={status.length ? "" : "Status"}
            />
          )}
        />
      )}

      <Button
        variant="outlined"
        startIcon={<CalendarMonthOutlinedIcon />}
        onClick={(e) => setAncora(e.currentTarget)}
        sx={styles.filtroPill(periodoAtivo)}
      >
        {rotuloPeriodo}
      </Button>

      {ativos > 0 && (
        <Tooltip title="Limpar filtros" arrow>
          <Badge
            badgeContent={ativos}
            color="primary"
            sx={{ "& .MuiBadge-badge": { top: 3, right: 3 } }}
          >
            <IconButton onClick={onLimpar} sx={styles.limparFiltros}>
              <FilterAltOffOutlinedIcon fontSize="small" />
            </IconButton>
          </Badge>
        </Tooltip>
      )}

      <Popover
        open={!!ancora}
        anchorEl={ancora}
        onClose={() => setAncora(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: styles.periodoPopover } }}
      >
        <Typography sx={styles.campoLabel}>Data de criação</Typography>
        <Box sx={styles.periodoPresets}>
          {PRESETS.map((preset) => (
            <Chip
              key={preset.label}
              size="small"
              label={preset.label}
              sx={styles.periodoPreset}
              onClick={() => onPeriodo(preset.resolver())}
            />
          ))}
        </Box>
        <Divider />
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
          <DatePicker
            label="De"
            format="DD/MM/YYYY"
            value={periodo.inicio ? dayjs(periodo.inicio) : null}
            onChange={(valor) =>
              onPeriodo({
                ...periodo,
                inicio: valor ? valor.format(FORMATO) : null,
              })
            }
            slotProps={{
              textField: { size: "small", fullWidth: true },
              field: { clearable: true },
            }}
          />
          <DatePicker
            label="Até"
            format="DD/MM/YYYY"
            value={periodo.fim ? dayjs(periodo.fim) : null}
            minDate={periodo.inicio ? dayjs(periodo.inicio) : undefined}
            onChange={(valor) =>
              onPeriodo({
                ...periodo,
                fim: valor ? valor.format(FORMATO) : null,
              })
            }
            slotProps={{
              textField: { size: "small", fullWidth: true },
              field: { clearable: true },
            }}
          />
        </LocalizationProvider>
        <Button
          size="small"
          disabled={!periodoAtivo}
          onClick={() => onPeriodo({ inicio: null, fim: null })}
          sx={{ textTransform: "none", alignSelf: "flex-end" }}
        >
          Limpar período
        </Button>
      </Popover>
    </>
  );
}
